import React, { useState } from 'react';
import styles from './PersonalityQuestionnaire.module.css';
import { preferencesService } from '../../../services/api';

const PersonalityQuestionnaire = ({ onComplete, onClose, initialAnswers = null }) => {
  const [currentQuestion, setCurrentQuestion] = useState(1);
  const [answers, setAnswers] = useState(
    initialAnswers || {
      involvement: '',
      priorities: [],
      communication: '',
      experience: '',
      approach: ''
    }
  );

  const questions = [
    {
      id: 1,
      key: 'involvement',
      title: 'How involved do you want to be in this project?',
      type: 'single',
      options: [
        { value: 'active', label: "I'll be checking in regularly and giving feedback" },
        { value: 'moderate', label: "I want updates but don't need to micromanage" },
        { value: 'hands-off', label: "I'd prefer to stay out of the way and let them handle it" }
      ]
    },
    {
      id: 2,
      key: 'priorities',
      title: 'What do you care about most when hiring a contractor?',
      subtitle: 'Select up to 2',
      type: 'multiple',
      maxSelections: 2,
      options: [
        { value: 'communication', label: 'Clear and timely communication' },
        { value: 'schedule', label: 'Sticking to the schedule' },
        { value: 'cleanliness', label: 'Respecting my home and keeping things clean' },
        { value: 'pricing', label: 'Transparent pricing and itemized estimates' },
        { value: 'independence', label: 'Taking initiative and working independently' },
        { value: 'explanation', label: 'Willingness to explain things clearly' }
      ]
    },
    {
      id: 3,
      key: 'communication',
      title: 'How do you prefer to communicate during the project?',
      type: 'single',
      options: [
        { value: 'text-email', label: 'Mostly through text or email' },
        { value: 'phone-face', label: 'I prefer phone calls or face-to-face updates' },
        { value: 'flexible', label: "I'm flexible and open to either" }
      ]
    },
    {
      id: 4,
      key: 'experience',
      title: 'Have you done a home project like this before?',
      type: 'single',
      options: [
        { value: 'experienced', label: "Yes — I've done several" },
        { value: 'some', label: 'Once or twice' },
        { value: 'first-time', label: "No — this is my first time" }
      ]
    },
    {
      id: 5,
      key: 'approach',
      title: 'Which approach feels right for you?',
      type: 'single',
      options: [
        { value: 'detailed', label: 'I want a detailed quote with everything clearly defined up front' },
        { value: 'flexible', label: "I'm okay with a flexible plan as long as we communicate" },
        { value: 'adaptive', label: 'I prefer to figure things out as the project moves forward' }
      ]
    }
  ];

  const currentQuestionData = questions.find(q => q.id === currentQuestion);
  const totalQuestions = questions.length;

  const handleAnswerChange = (questionKey, value) => {
    if (currentQuestionData.type === 'multiple') {
      setAnswers(prev => {
        const currentAnswers = Array.isArray(prev[questionKey]) ? prev[questionKey] : [];
        const newAnswers = currentAnswers.includes(value)
          ? currentAnswers.filter(answer => answer !== value)
          : currentAnswers.length < currentQuestionData.maxSelections
            ? [...currentAnswers, value]
            : currentAnswers;
        
        return { ...prev, [questionKey]: newAnswers };
      });
    } else {
      setAnswers(prev => ({ ...prev, [questionKey]: value }));
    }
  };

  const handleNext = () => {
    if (currentQuestion < totalQuestions) {
      setCurrentQuestion(prev => prev + 1);
    } else {
      handleComplete();
    }
  };

  const handleSkip = () => {
    if (currentQuestion < totalQuestions) {
      setCurrentQuestion(prev => prev + 1);
    } else {
      handleComplete();
    }
  };

  const handleComplete = async () => {
    try {
      // Save preferences to backend
      await preferencesService.savePreferences(answers);
      onComplete(answers);
    } catch (error) {
      console.error('Error saving preferences:', error);
      throw error; // Let the error bubble up instead of falling back
    }
  };

  const isAnswered = () => {
    const answer = answers[currentQuestionData.key];
    if (currentQuestionData.type === 'multiple') {
      return Array.isArray(answer) && answer.length > 0;
    }
    return answer && answer.length > 0;
  };

  const renderFinalScreen = () => (
    <div className={styles.finalScreen}>
      <div className={styles.successIcon}>
        <i className="fas fa-check-circle"></i>
      </div>
      <h2>
        {initialAnswers 
          ? "Preferences updated successfully!" 
          : "Thanks! Your answers will help us recommend contractors that match your style."}
      </h2>
      <p className={styles.finalNote}>
        You can update these preferences anytime from your dashboard settings.
      </p>
      <button className={styles.finalButton} onClick={handleComplete}>
        <i className={`fas ${initialAnswers ? 'fa-check' : 'fa-rocket'}`}></i>
        {initialAnswers ? "Save Changes" : "Let's Get Started"}
      </button>
    </div>
  );

  if (currentQuestion > totalQuestions) {
    return (
      <div className={styles.overlay}>
        <div className={styles.container}>
          <button className={styles.closeButton} onClick={onClose}>
            <i className="fas fa-times"></i>
          </button>
          {renderFinalScreen()}
        </div>
      </div>
    );
  }

  return (
    <div className={styles.overlay}>
      <div className={styles.container}>
        <button className={styles.closeButton} onClick={onClose}>
          <i className="fas fa-times"></i>
        </button>
        
        <div className={styles.header}>
          <h1>
            {initialAnswers 
              ? "Update your contractor preferences" 
              : "Help us personalize your contractor recommendations"}
          </h1>
          <p>
            {initialAnswers 
              ? "Update your answers to better match you with contractors. Takes under 2 minutes."
              : "Answer 5 quick questions so we can better match you with the right style of contractor. Takes under 2 minutes."}
          </p>
        </div>

        <div className={styles.progress}>
          <div className={styles.progressBar}>
            <div 
              className={styles.progressFill} 
              style={{ width: `${(currentQuestion / totalQuestions) * 100}%` }}
            ></div>
          </div>
          <span className={styles.progressText}>{currentQuestion} of {totalQuestions}</span>
        </div>

        <div className={styles.questionContainer}>
          <div className={styles.questionNumber}>Question {currentQuestion}</div>
          <h2 className={styles.questionTitle}>{currentQuestionData.title}</h2>
          {currentQuestionData.subtitle && (
            <p className={styles.questionSubtitle}>{currentQuestionData.subtitle}</p>
          )}

          <div className={styles.optionsContainer}>
            {currentQuestionData.options.map((option, index) => (
              <label key={index} className={styles.optionLabel}>
                <input
                  type={currentQuestionData.type === 'multiple' ? 'checkbox' : 'radio'}
                  name={currentQuestionData.key}
                  value={option.value}
                  checked={
                    currentQuestionData.type === 'multiple'
                      ? (Array.isArray(answers[currentQuestionData.key]) && answers[currentQuestionData.key].includes(option.value))
                      : answers[currentQuestionData.key] === option.value
                  }
                  onChange={() => handleAnswerChange(currentQuestionData.key, option.value)}
                  className={styles.optionInput}
                />
                <div className={styles.optionContent}>
                  <div className={styles.optionIndicator}>
                    {currentQuestionData.type === 'multiple' ? (
                      <div className={styles.checkbox}>
                        <i className="fas fa-check"></i>
                      </div>
                    ) : (
                      <div className={styles.radio}></div>
                    )}
                  </div>
                  <span className={styles.optionText}>{option.label}</span>
                </div>
              </label>
            ))}
          </div>
        </div>

        <div className={styles.footer}>
          {/* <button className={styles.skipButton} onClick={handleSkip}>
            Skip
          </button> */}
          <button 
            className={`${styles.nextButton} ${isAnswered() ? styles.enabled : ''}`}
            onClick={handleNext}
            disabled={!isAnswered()}
          >
            {currentQuestion === totalQuestions ? 'Complete' : 'Next'}
            <i className="fas fa-arrow-right"></i>
          </button>
        </div>

        <div className={styles.disclaimerNote}>
          <i className="fas fa-info-circle"></i>
          You can change these preferences later from your dashboard settings.
        </div>
      </div>
    </div>
  );
};

export default PersonalityQuestionnaire; 